import React, {useEffect, useState, useRef} from "react";
import Icon from "../ui/Icon";
import Stack from "../ui/Stack";
import SearchInput from "../common/form/SearchInput";

const Filters = ({items, onFilterChange}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [searchTags, setSearchTags] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [tags, setTags] = useState([]);
    const dropdownRef = useRef(null);
    const searchRef = useRef(null);

    useEffect(() => {
        if (items && Array.isArray(items)) {
            const tagNames = items
                .flatMap(item => item.tags || [])
                .map(tagObj => tagObj.name);

            const uniqueTagNames = [...new Set(tagNames)].sort((a, b) =>
                a.localeCompare(b)
            );

            setTags(uniqueTagNames);
        }
    }, [items]);

    useEffect(() => {
        const filtered = items.filter(product => {
            const isSearchMatch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
            const isTagMatch =
                !searchTags || searchTags.length === 0
                    ? true
                    : product.tags.some(tag => searchTags.includes(tag.name));
            return isSearchMatch && isTagMatch;
        });
        onFilterChange(filtered);
    }, [searchTerm, searchTags, items, onFilterChange]);

    const handleTagChange = (tag) => {
        setSearchTags(prev =>
            prev.includes(tag)
                ? prev.filter(t => t !== tag)
                : [...prev, tag]
        );
    };
    const handleClear = () => {
        setSearchTerm("");
        setSearchTags([]);
    };

    const searchTagHandler = (val) => {
        setSearchText(val);

        const tagNames = items
            .flatMap(item => item.tags || [])
            .map(tagObj => tagObj.name);

        // Get only unique tag names
        const uniqueTagNames = [...new Set(tagNames)];

        // Filter and sort tags that match the typed input (case-insensitive)
        const filteredTags = uniqueTagNames
            .filter(tag => tag.toLowerCase().includes(val.toLowerCase()))
            .sort((a, b) => a.localeCompare(b)); // Alphabetical sort

        setTags(filteredTags);
    };

    //focus on filter search when dropdown is clicked
    useEffect(() => {
        const dropdownEl = dropdownRef.current;

        if (!dropdownEl) return;

        const handleDropdownShown = () => {
            if (searchRef.current) {
                searchRef.current.focus(); // manually focus search input
            }
        };

        dropdownEl.addEventListener("shown.bs.dropdown", handleDropdownShown);

        return () => {
            dropdownEl.removeEventListener("shown.bs.dropdown", handleDropdownShown);
        };
    }, []);

    return (
        <div className="position-sticky bg-white pb-4 z-3" style={{top: "53px", boxShadow: "0 0 0 4px #fff"}}>
            <form className="d-flex justify-content-md-between align-items-start gap-2 flex-wrap mt-1" role="search">
                <input
                    id={"searchInput"}
                    className="form-control me-2 w-auto fs-sm"
                    style={{height: "fit-content"}}
                    type="search"
                    placeholder="Search..."
                    aria-label="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                />
                <Stack className="gap-3 ms-auto">
                    <div className="dropdown" ref={dropdownRef}>
                        <button
                            className="btn bg-primary bg-opacity-15 bg-opacity-25-hover py-2 px-4 d-flex align-items-center gap-2 fw-medium border-0"
                            type="button"
                            aria-expanded="false"
                            data-bs-auto-close="outside"
                            data-bs-toggle="dropdown"
                        >
                            <Icon name="filter" width="14px"/>
                            <span
                                className="d-none d-xl-inline"> Filter Tags
                            </span>
                            {searchTags.length > 0 && <>({searchTags.length})</>}
                        </button>

                        <ul className="dropdown-menu dropdown-menu-end p-0" style={{height: '300px', width: '250px'}}>
                            <div className="search-wrapper bg-white p-2 sticky-top">
                                <SearchInput
                                    ref={searchRef}
                                    value={searchText}
                                    onChange={(e) => searchTagHandler(e.target.value)}
                                    placeholder="Search by tags"
                                    className="mb-4"
                                    isSearchStyle="true" //set to true only when searchInput is placed inside multiselect(for design purpose)
                                />
                            </div>
                            {tags.map((tag, index) => (
                                <li key={index} className="dropdown-item p-0 m-0">
                                    <label
                                        className="form-label d-flex align-items-center column-gap-2 px-3 py-1 mb-0 user-select-none"
                                        htmlFor={`check-${index}`} role="button">
                                        <input
                                            className="form-check-input m-0"
                                            type="checkbox"
                                            id={`check-${index}`}
                                            checked={searchTags.includes(tag)}
                                            onChange={() => handleTagChange(tag)}
                                        />
                                        <span>{tag}</span>
                                    </label>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <button
                        className="btn bg-secondary bg-opacity-15 bg-opacity-25-hover py-2 px-4 d-flex align-items-center gap-2 fw-medium border-0"
                        type="button"
                        onClick={handleClear}
                    >
                        <Icon name="clearFilter" width="20" height="20"/>
                    </button>
                </Stack>
            </form>
        </div>
    );
};

export default Filters;
